import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { courseInfoAtom } from '@/atoms/courseInfo'
import { setPrettyNameAndAbbreviationForCourse } from '@/api/cb/contentCreatorContent'
import { syncErrorMessage } from '@/utils/syncMessages'
import styles from './PrettyNameAndAbbreviationEditor.module.less'
import { Button, Input } from 'antd'

export const PrettyNameAndAbbreviationEditor: React.FC<
    unknown
> = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const [courseInfo, setCourseInfo] = useRecoilState(
        courseInfoAtom(courseName)
    )
    const { prettyName, abbreviation } = courseInfo
    const [currentPrettyName, setCurrentPrettyName] = useState(prettyName)
    const [currentAbbreviation, setCurrentAbbreviation] = useState(abbreviation)

    const hasUnsavedChanges = useMemo((): boolean => {
        return (
            currentPrettyName !== prettyName ||
            currentAbbreviation !== abbreviation
        )
    }, [prettyName, abbreviation, currentPrettyName, currentAbbreviation])

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const saveChanges = useCallback(async (): Promise<void> => {
        setIsLoading(true)
        // Call the API to save the changes
        const response = await setPrettyNameAndAbbreviationForCourse({
            name: courseName,
            prettyName: currentPrettyName,
            abbreviation: currentAbbreviation,
        })
        if (response.data.isError) {
            syncErrorMessage(
                'Error updating pretty name / abbreviation. You may want to try setting it again or refreshing the page.'
            )
        } else {
            setCourseInfo((courseInfo) => ({
                ...courseInfo,
                prettyName: currentPrettyName,
                abbreviation: currentAbbreviation,
            }))
        }
        setIsLoading(false)
    }, [courseName, currentAbbreviation, currentPrettyName, setCourseInfo])

    return (
        <div className={styles.prettyNameAndAbbreviationEditor}>
            <div className={styles.text}>Pretty Name:</div>
            <Input
                value={currentPrettyName}
                onChange={(e) => {
                    setCurrentPrettyName(e.target.value)
                }}
                disabled={isReadOnly}
            />
            <div className={styles.text}>Abbreviation:</div>
            <Input
                value={currentAbbreviation}
                onChange={(e) => {
                    setCurrentAbbreviation(e.target.value)
                }}
                disabled={isReadOnly}
            />
            <Button
                onClick={saveChanges}
                type={'primary'}
                loading={isLoading}
                disabled={isReadOnly || !hasUnsavedChanges}
            >
                Save
            </Button>
        </div>
    )
}
